/**
 * Invoice detail — the anchor screen for billing.
 *
 * Everything about one invoice hangs off this page: its lines while it is a draft, its
 * credit notes once issued, and the payments recorded against it. Wired to the real
 * `InvoiceViewSet` (`domains/finance/views.py`), which has been complete, tested and
 * reachable from no interface until this screen.
 *
 * ===========================================================================
 * WHAT GATES WHAT
 * ===========================================================================
 * `_actions.edit` on the payload is the accurate answer for issuing, sending, adding or
 * removing a line, and issuing a credit note — `InvoiceViewSet.kernel_action()` maps
 * every one of them onto `invoice.edit`, deliberately separate from `invoice.create`.
 * That is what stops an Account Executive, who holds `create` so a quote can be
 * drafted, from also being able to issue or bill one. Every one of those controls below
 * is gated on `<Can actions={invoice._actions} action="edit">`, never on a bare
 * capability check, because `_actions` is computed against THIS record's scope.
 *
 * Recording a payment is a different module (`payment.create`) with no record yet to
 * scope against, so it is gated with `useCan('payment', 'create')` instead — the same
 * shape `PaymentQueuePage` uses for `verify`/`reject`.
 *
 * Line controls additionally hide once the invoice is no longer a draft, on top of the
 * capability gate: the backend already refuses with a 409 naming the reason
 * (`invoice_issued`), but CLAUDE.md's own lesson is that a dead control that waits for
 * the server to say no is still a bug the first time somebody opens the screen.
 * ===========================================================================
 */

import {
  ConflictError,
  STAFF,
  newIdempotencyKey,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  usePaginatedList,
  type ClientDetail,
  type ClientFinancialPosition,
  type Invoice,
  type Payment,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  ErrorState,
  Form,
  FormActions,
  FormInput,
  Skeleton,
  StatusPill,
  statusTone,
  useToast,
  useZodForm,
} from '@legalcrm/ui';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Can, useCan } from '@/auth/permissions';
import { formatDate, formatDateTime, formatMoney, humanise } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

import { ClientPaymentDetailsCard } from './ClientPaymentDetailsCard';
import { IssueCreditNoteDialog } from './IssueCreditNoteDialog';
import { RecordPaymentDialog } from './RecordPaymentDialog';

/** As `PaymentQueuePage`'s local map — payment statuses are not all in `statusTone()`. */
const PAYMENT_TONES: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  verified: 'success',
  proof_submitted: 'warning',
  under_review: 'info',
  pending: 'neutral',
  rejected: 'danger',
  cancelled: 'neutral',
  refunded: 'neutral',
  partially_refunded: 'warning',
};

export function InvoiceDetailPage() {
  const { invoiceId = '' } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const api = useApi();

  const [issuing, setIssuing] = useState(false);
  const [sending, setSending] = useState(false);
  const [crediting, setCrediting] = useState(false);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const canEditFirm = useCan('organization', 'edit');

  const query = useApiQuery<Invoice>(qk.detail('invoices', invoiceId), STAFF.invoice(invoiceId), {
    enabled: Boolean(invoiceId),
  });
  const invoice = query.data;

  const client = useApiQuery<ClientDetail>(
    qk.detail('clients', invoice?.client ?? ''),
    STAFF.client(invoice?.client ?? ''),
    { enabled: Boolean(invoice?.client) },
  );

  const payments = usePaginatedList<Payment>(
    qk.list('payments', { invoice: invoiceId }),
    STAFF.payments,
    { invoice: invoiceId },
    { enabled: Boolean(invoiceId) },
  );

  const position = useApiQuery<ClientFinancialPosition>(
    qk.detail('financial-position', invoice?.client ?? ''),
    STAFF.clientFinancialPosition(invoice?.client ?? ''),
    {
      query: invoice?.case ? { case: invoice.case } : undefined,
      enabled: Boolean(invoice?.client),
      // A position 404s for a client with nothing this reader may see, which cannot
      // happen once the invoice itself has loaded — but the card is a courtesy, not
      // the reason this screen exists, so a failure here should not blank the page.
      retry: false,
    },
  );

  // Idempotency keys: one per user-initiated attempt, minted when the confirmation
  // opens and reused across retries of that same attempt (see `PaymentQueuePage`).
  const issueKey = useRef(newIdempotencyKey());
  useEffect(() => {
    if (issuing) issueKey.current = newIdempotencyKey();
  }, [issuing]);
  const sendKey = useRef(newIdempotencyKey());
  useEffect(() => {
    if (sending) sendKey.current = newIdempotencyKey();
  }, [sending]);

  const issue = useApiMutation<Invoice, void>(
    {
      mutationFn: () =>
        api.post<Invoice>(STAFF.invoiceIssue(invoiceId), undefined, {
          idempotencyKey: issueKey.current,
        }),
      invalidates: [qk.detail('invoices', invoiceId), qk.all('invoices')],
    },
    {
      onSuccess: () => {
        toast({ title: 'Invoice issued', tone: 'success' });
        setIssuing(false);
      },
      onError: (error) => {
        toast({
          title: 'Could not issue this invoice',
          description: error instanceof ConflictError ? error.detail : 'Try again.',
          tone: 'warning',
        });
      },
    },
  );

  const send = useApiMutation<Invoice, void>(
    {
      mutationFn: () =>
        api.post<Invoice>(STAFF.invoiceSend(invoiceId), undefined, {
          idempotencyKey: sendKey.current,
        }),
      invalidates: [qk.detail('invoices', invoiceId), qk.all('invoices')],
    },
    {
      onSuccess: () => {
        toast({ title: 'Invoice sent to the client', tone: 'success' });
        setSending(false);
      },
      onError: (error) => {
        toast({
          title: 'Could not send this invoice',
          description: error instanceof ConflictError ? error.detail : 'Try again.',
          tone: 'warning',
        });
      },
    },
  );

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <span className="sr-only">Loading invoice</span>
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (query.error || !invoice) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const isDraft = invoice.status === 'draft';
  const canSend = invoice.status === 'issued' || invoice.status === 'sent';
  const canCreditOrPay = !isDraft && invoice.status !== 'cancelled';

  return (
    <>
      <PageHeader
        title={invoice.invoice_number}
        description={client.data ? client.data.display_name : undefined}
        meta={
          <>
            <StatusPill label={humanise(invoice.status)} tone={statusTone(invoice.status)} />
            {invoice.is_overdue && (
              <Badge tone="danger" size="sm">
                Overdue
              </Badge>
            )}
            {invoice.client && (
              <Link to={`/clients/${invoice.client}`} className="text-sm text-primary hover:underline">
                View client
              </Link>
            )}
            {invoice.case && (
              <Link to={`/cases/${invoice.case}`} className="text-sm text-primary hover:underline">
                View matter
              </Link>
            )}
          </>
        }
        actions={
          <>
            <Can actions={invoice._actions} action="edit">
              {isDraft && (
                <Button variant="primary" onClick={() => setIssuing(true)}>
                  Issue
                </Button>
              )}
              {canSend && (
                <Button variant="secondary" onClick={() => setSending(true)}>
                  {invoice.status === 'sent' ? 'Resend to client' : 'Send to client'}
                </Button>
              )}
              {canCreditOrPay && (
                <Button variant="secondary" onClick={() => setCrediting(true)}>
                  Issue credit note
                </Button>
              )}
            </Can>
            {canCreditOrPay && (
              <Can module="payment" action="create">
                <Button variant="secondary" onClick={() => setRecordingPayment(true)}>
                  Record payment
                </Button>
              </Can>
            )}
          </>
        }
      />

      <ConfirmModal
        open={issuing}
        onClose={() => setIssuing(false)}
        title="Issue this invoice?"
        confirmLabel="Issue"
        loading={issue.isPending}
        onConfirm={() => issue.mutate()}
        consequences={
          <ul className="list-inside list-disc text-sm text-ink-muted">
            <li>freezes the invoice — from here, corrections are credit notes only</li>
            <li>allocates its invoice number, permanently</li>
            <li>the client is not told yet; sending is a separate, later step</li>
          </ul>
        }
      />

      <ConfirmModal
        open={sending}
        onClose={() => setSending(false)}
        title={invoice.status === 'sent' ? 'Resend this invoice?' : 'Send this invoice?'}
        confirmLabel="Send"
        loading={send.isPending}
        onConfirm={() => send.mutate()}
        consequences={
          <p className="text-sm text-ink-muted">
            Tells the client the invoice is theirs to pay, by email (and WhatsApp where
            configured).
          </p>
        }
      />

      {invoice.client && (
        <IssueCreditNoteDialog
          invoice={invoice}
          open={crediting}
          onClose={() => setCrediting(false)}
        />
      )}
      {invoice.client && (
        <RecordPaymentDialog
          invoice={invoice}
          open={recordingPayment}
          onClose={() => setRecordingPayment(false)}
        />
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <Card>
            <CardHeader title="Summary" />
            <CardBody>
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Amount label="Subtotal" value={formatMoney(invoice.subtotal, invoice.currency)} />
                <Amount label="Tax" value={formatMoney(invoice.tax_total, invoice.currency)} />
                <Amount label="Total" value={formatMoney(invoice.total, invoice.currency)} emphasis />
                <Amount label="Paid" value={formatMoney(invoice.amount_paid, invoice.currency)} />
              </dl>
              <dl className="mt-4 flex flex-col gap-2 border-t pt-3">
                <Row label="Balance">
                  <span className={Number(invoice.balance) > 0 ? 'font-semibold text-ink' : 'text-ink-muted'}>
                    {formatMoney(invoice.balance, invoice.currency)}
                  </span>
                </Row>
                <Row label="Issued">{formatDate(invoice.issue_date)}</Row>
                <Row label="Due">{formatDate(invoice.due_date)}</Row>
              </dl>
            </CardBody>
          </Card>

          <InvoiceLinesCard invoice={invoice} isDraft={isDraft} />

          <Card>
            <CardHeader title="Credit notes" />
            <CardBody>
              {invoice.credit_notes.length === 0 ? (
                <p className="text-sm text-ink-subtle">None issued.</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {invoice.credit_notes.map((note) => (
                    <li key={note.id} className="rounded border px-3 py-2 text-sm">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-mono text-xs text-ink-muted">
                          {note.credit_note_number}
                        </span>
                        <span className="font-medium tabular-nums text-ink">
                          {formatMoney(note.amount, note.currency)}
                        </span>
                      </div>
                      <p className="mt-1 text-ink-muted">{note.reason}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Payments"
              actions={
                <Button variant="ghost" onClick={() => navigate('/payments')}>
                  Verification queue
                </Button>
              }
            />
            <CardBody>
              {payments.isLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : payments.items.length === 0 ? (
                <p className="text-sm text-ink-subtle">No payments recorded against this invoice.</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {payments.items.map((payment) => (
                    <li key={payment.id} className="rounded border px-3 py-2 text-sm">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-medium tabular-nums text-ink">
                          {formatMoney(payment.amount, payment.currency)}
                        </span>
                        <StatusPill
                          label={humanise(payment.status)}
                          tone={PAYMENT_TONES[payment.status] ?? statusTone(payment.status)}
                        />
                      </div>
                      <p className="mt-1 text-ink-muted">
                        {humanise(payment.method)}
                        {payment.reference ? ` · ${payment.reference}` : ''} ·{' '}
                        {formatDateTime(payment.received_at)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="flex flex-col gap-4">
          {!isDraft && (
            <ClientPaymentDetailsCard
              details={invoice.client_payment_details}
              canEditFirm={canEditFirm}
            />
          )}
          {invoice.client && position.data && (
            <Card>
              <CardHeader title="Client financial position" />
              <CardBody>
                <dl className="flex flex-col gap-2">
                  <Row label="Outstanding">
                    {formatMoney(position.data.outstanding_balance, position.data.currency)}
                  </Row>
                  <Row label="Overdue">
                    {formatMoney(position.data.overdue_balance, position.data.currency)}
                  </Row>
                  <Row label="Paid to date">
                    {formatMoney(position.data.paid_to_date, position.data.currency)}
                  </Row>
                  <Row label="Invoiced to date">
                    {formatMoney(position.data.invoiced_to_date, position.data.currency)}
                  </Row>
                  <Row label="Credited to date">
                    {formatMoney(position.data.credited_to_date, position.data.currency)}
                  </Row>
                </dl>
                <p className="mt-3 text-xs text-ink-subtle">
                  {invoice.case
                    ? 'Narrowed to this matter.'
                    : 'Across every matter this client has with the firm.'}{' '}
                  Unallocated receipts are not counted (this product does not do trust
                  accounting).
                </p>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Lines while the invoice is a draft; a fixed record once issued.
 *
 * The add-line form and every remove button are hidden the moment `isDraft` is false —
 * not merely disabled — because the backend refuses the request either way and a
 * control that waits to be told no is a worse interface than one that was never drawn.
 */
function InvoiceLinesCard({ invoice, isDraft }: { invoice: Invoice; isDraft: boolean }) {
  return (
    <Card>
      <CardHeader title="Lines" />
      <CardBody>
        {invoice.lines.length === 0 ? (
          <p className="text-sm text-ink-subtle">
            {isDraft ? 'No lines yet.' : 'This invoice has no lines.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-ink-subtle">
                  <th className="py-1.5 pr-3 font-medium">Description</th>
                  <th className="py-1.5 pr-3 text-right font-medium">Qty</th>
                  <th className="py-1.5 pr-3 text-right font-medium">Unit</th>
                  <th className="py-1.5 pr-3 text-right font-medium">Tax %</th>
                  <th className="py-1.5 pr-3 text-right font-medium">Amount</th>
                  {isDraft && <th className="py-1.5" />}
                </tr>
              </thead>
              <tbody>
                {invoice.lines.map((line) => (
                  <tr key={line.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-ink">{line.description}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-ink-muted">
                      {line.quantity}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums text-ink-muted">
                      {formatMoney(line.unit_amount, invoice.currency)}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums text-ink-muted">
                      {line.tax_rate}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums font-medium text-ink">
                      {formatMoney(line.amount, invoice.currency)}
                    </td>
                    {isDraft && (
                      <td className="py-2 text-right">
                        <RemoveLineButton invoiceId={invoice.id} lineId={line.id} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {isDraft && (
          <Can actions={invoice._actions} action="edit">
            <AddLineForm invoiceId={invoice.id} />
          </Can>
        )}
      </CardBody>
    </Card>
  );
}

function RemoveLineButton({ invoiceId, lineId }: { invoiceId: string; lineId: string }) {
  const api = useApi();
  const remove = useApiMutation<void, void>({
    mutationFn: () => api.delete<void>(STAFF.invoiceLine(invoiceId, lineId)),
    invalidates: [qk.detail('invoices', invoiceId), qk.all('invoices')],
  });

  return (
    <Button
      variant="ghost"
      size="sm"
      loading={remove.isPending}
      onClick={() => remove.mutate()}
    >
      Remove
    </Button>
  );
}

const lineSchema = z.object({
  description: z.string().trim().min(1, 'Describe the line'),
  quantity: z.string().trim().min(1, 'Enter a quantity'),
  unit_amount: z
    .string()
    .trim()
    .min(1, 'Enter an amount')
    .refine((value) => Number(value) >= 0, 'Enter a valid amount'),
  tax_rate: z.string().trim(),
});

type LineValues = z.infer<typeof lineSchema>;

function AddLineForm({ invoiceId }: { invoiceId: string }) {
  const api = useApi();
  const form = useZodForm(lineSchema, {
    defaultValues: { description: '', quantity: '1', unit_amount: '', tax_rate: '0' },
  });

  const add = useApiMutation<Invoice, LineValues>({
    mutationFn: (values) =>
      api.post<Invoice>(STAFF.invoiceLines(invoiceId), {
        description: values.description,
        quantity: values.quantity,
        unit_amount: values.unit_amount,
        tax_rate: values.tax_rate || '0',
      }),
    invalidates: [qk.detail('invoices', invoiceId), qk.all('invoices')],
  });

  return (
    <div className="mt-4 border-t pt-4">
      <Form
        form={form}
        ariaLabel="Add a line"
        error={add.error}
        onSubmit={async (values) => {
          await add.mutateAsync(values);
          form.reset({ description: '', quantity: '1', unit_amount: '', tax_rate: '0' });
        }}
        className="gap-3"
      >
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <FormInput form={form} name="description" label="Description" hideLabel placeholder="Description" />
          </div>
          <FormInput form={form} name="quantity" label="Qty" hideLabel placeholder="Qty" inputMode="decimal" />
          <FormInput
            form={form}
            name="unit_amount"
            label="Unit amount"
            hideLabel
            placeholder="Unit amount"
            inputMode="decimal"
          />
        </div>
        <div className="flex items-end gap-3">
          <div className="w-32">
            <FormInput
              form={form}
              name="tax_rate"
              label="Tax %"
              hideLabel
              placeholder="Tax %"
              inputMode="decimal"
            />
          </div>
          <FormActions submitLabel="Add line" busy={add.isPending} className="flex-1" />
        </div>
      </Form>
    </div>
  );
}

function Amount({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className={emphasis ? 'text-lg font-semibold tabular-nums text-ink' : 'tabular-nums text-ink'}>
        {value}
      </dd>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className="text-right text-base text-ink">{children}</dd>
    </div>
  );
}
